<template>
  <div class="sign-off">
    <div class="heading">
      <span class="title">Sign-off {{ props.shift?.winning_claim_employee_name.split(' ')[0] }}'s shift</span>
      <span class="date">{{ formatDate(props.shift?.start_at) }}</span>
    </div>
    <div class="sign-off-container">
      <div class="shift-results">
        <div class="shift-result">
          <h3>Details</h3>
          <div class="time-confirm">
            <div class="toggle">
              <span>Did {{ props.shift?.winning_claim_employee_name.split(' ')[0] }} start and finish on time?</span>
              <div>
                <b-radio name="time-confirm" value="yes" v-model="finishedOnTime"> Yes </b-radio>
                <b-radio name="time-confirm" value="no" v-model="finishedOnTime"> No </b-radio>
              </div>
            </div>
            <div v-if="finishedOnTime === 'no'" class="time-select">
              <div class="labels">
                <span>Start time</span>
                <span>End time</span>
              </div>
              <div>
                <vue-slider
                  :railStyle="{ background: 'var(--palette-color-default-lighten-90)' }"
                  :processStyle="{ background: 'var(--palette-color-brand-primary)' }"
                  tooltip="none"
                  v-model="startEnd"
                  :enable-cross="false"
                  :max="totalMinutes"
                ></vue-slider>
              </div>
              <div class="times">
                <div>
                  <span :class="{ not: startEnd[0] !== 0 }" class="expected">{{
                    formatTime(props.shift?.start_at)
                  }}</span>
                  <span v-if="startEnd[0] !== 0" class="actual">{{ formatTime(startTime) }}</span>
                </div>
                <div>
                  <span :class="{ not: startEnd[1] !== totalMinutes }" class="expected">{{
                    formatTime(props.shift?.end_at)
                  }}</span>
                  <span v-if="startEnd[1] !== totalMinutes" class="actual">{{ formatTime(endTime) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="employee-result">
          <div>
            <h3>How was {{ props.shift?.winning_claim_employee_name.split(' ')[0] }}'s shift today?</h3>
            <span class="star-explainer">Click a star to rate from 1 to 5</span>
          </div>
          <EmployeeAvatar :imageUrl="props.shift?.winning_claim_employee_photo_url" :size="80" />
          <div class="rating">
            <div class="stars">
              <svg
                v-for="index in 5"
                :key="'star-' + index"
                :class="['rating-star', { active: index <= rating }]"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                @click="selectRating(index)"
              >
                <path
                  d="M22.425 9.13125C22.3311 8.83592 22.1502 8.57585 21.9059 8.38513C21.6617 8.19441 21.3655 8.08195 21.0562 8.0625L15.4875 7.67813L13.425 2.475C13.3124 2.1885 13.1163 1.94241 12.8623 1.76858C12.6082 1.59475 12.3078 1.50118 12 1.5C11.6921 1.50118 11.3917 1.59475 11.1377 1.76858C10.8836 1.94241 10.6876 2.1885 10.575 2.475L8.47497 7.70625L2.94372 8.0625C2.63482 8.08321 2.33924 8.19614 2.09522 8.38668C1.8512 8.57722 1.66997 8.83659 1.57497 9.13125C1.47742 9.43043 1.47172 9.75197 1.55861 10.0544C1.6455 10.3569 1.821 10.6263 2.06247 10.8281L6.31872 14.4281L5.0531 19.4062C4.96553 19.743 4.98129 20.0983 5.09832 20.426C5.21536 20.7537 5.42826 21.0386 5.70935 21.2438C5.98219 21.4396 6.30726 21.5495 6.64295 21.5596C6.97864 21.5696 7.30969 21.4792 7.59372 21.3L11.9906 18.5156H12.0093L16.7437 21.5062C16.9866 21.6641 17.2697 21.7487 17.5593 21.75C17.7958 21.7481 18.0287 21.6921 18.2402 21.5861C18.4516 21.4802 18.6359 21.3272 18.779 21.1389C18.922 20.9505 19.02 20.7319 19.0654 20.4998C19.1107 20.2677 19.1022 20.0283 19.0406 19.8L17.7 14.3531L21.9375 10.8281C22.1789 10.6263 22.3544 10.3569 22.4413 10.0544C22.5282 9.75197 22.5225 9.43043 22.425 9.13125Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <span class="label">{{ getStarRatingDescription(rating) }}</span>
          </div>
          <div class="tags-container">
            <span>What was positive?</span>
            <div class="review-tags">
              <div
                :class="['tag', { active: selectedReviewTags.includes(tag) }]"
                v-for="tag in ratingStarReviewTags[rating || 5]"
                :key="tag"
              >
                <input
                  type="checkbox"
                  :id="'tag-' + tag"
                  :value="tag"
                  @change="toggleReviewTag(tag)"
                  :checked="selectedReviewTags.includes(tag)"
                />
                <label :for="'tag-' + tag">{{ tag }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer>
        <div class="user-details">
          <EmployeeAvatar :imageUrl="props.shift?.winning_claim_employee_photo_url" />
          <div class="details">
            <span class="name">{{ props.shift?.winning_claim_employee_name }}</span>
            <span class="role">{{ props.shift?.portal_role_name }}</span>
          </div>
        </div>
        <div class="confirmation">
          <div class="pay">
            <span class="new-total">£{{ toRawCurrency(props.shift?.client_rate * props.shift?.hours) }}</span>
          </div>
          <b-button
            :disabled="signOffLoading || rating === 0"
            @click="signOffShift"
            class="sign-off-button"
            variant="primary"
          >
            <b-spinner v-if="signOffLoading" class="mr-1" small />
            <svg
              v-else
              class="mr-1"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12Z"
                fill="currentColor"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M16.8486 9.05973C17.2298 9.45934 17.2149 10.0923 16.8153 10.4736L11.3121 15.7236C10.9255 16.0924 10.3173 16.0921 9.93099 15.723L7.18411 13.098C6.78483 12.7164 6.77047 12.0834 7.15204 11.6841C7.5336 11.2848 8.1666 11.2705 8.56589 11.652L10.6225 13.6174L15.4347 9.02645C15.8343 8.64522 16.4673 8.66012 16.8486 9.05973Z"
                fill="currentColor"
              />
            </svg>
            Confirm and sign-off
          </b-button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router/composables';
import ApiClient from '@/ApiClient';
import { computed, ref, watchEffect } from 'vue';
import { toRawCurrency } from '@/utils';
import EmployeeAvatar from '@/pages/talent-pool/components/EmployeeAvatar.vue';
import VueSlider from 'vue-slider-component';
import { ratingStarReviewTags, ratingStarDescriptions } from '@/pages/talent-pool/utils/ratings';
import 'vue-slider-component/theme/default.css';
import { addMinutes, subMinutes, formatISO } from 'date-fns';
import { formatDate, timeToMinutes } from '@/pages/talent-pool/utils/datetime';
import { getShiftsForTab } from '@/state/composables/useTalentPoolShifts';

const props = defineProps(['shift']);

const route = useRoute();
const router = useRouter();
const locationId = route.params.locationId;
const tab = route.params.tab;

const signOffLoading = ref(false);
const rating = ref(0);
const finishedOnTime = ref('yes');
const selectedReviewTags = ref([]);
const startEnd = ref([0, 100]); // This will hold the slider values
const totalMinutes = ref(0);

// Watch the shift data to set the initial range for the slider
watchEffect(() => {
  if (props.shift) {
    const startMinutes = timeToMinutes(props.shift?.start_at);
    const endMinutes = timeToMinutes(props.shift?.end_at);
    totalMinutes.value = endMinutes - startMinutes;
    startEnd.value = [0, totalMinutes.value];
  }
});

// Computed properties to show the formatted time for the slider handles
const startTime = computed(() => {
  const minutesToAdd = startEnd.value[0];
  const startDate = new Date(props.shift?.start_at);
  const adjustedStart = addMinutes(startDate, minutesToAdd);
  return formatISO(adjustedStart);
});

const endTime = computed(() => {
  const minutesToSubtract = totalMinutes.value - startEnd.value[1];
  const endDate = new Date(props.shift?.end_at);
  const adjustedEnd = subMinutes(endDate, minutesToSubtract);
  return formatISO(adjustedEnd);
});

const toggleReviewTag = tag => {
  const index = selectedReviewTags.value.indexOf(tag);
  if (index === -1) {
    selectedReviewTags.value.push(tag);
  } else {
    selectedReviewTags.value.splice(index, 1);
  }
};

function formatTime(datetimeStr) {
  const date = new Date(datetimeStr);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
}

function getStarRatingDescription(rating) {
  if (rating === null || rating === 0) {
    return 'Please rate';
  } else {
    return ratingStarDescriptions[rating] || 'Invalid rating'; // Fallback for out-of-range ratings
  }
}

const selectRating = rate => {
  rating.value = rate;
  selectedReviewTags.value = [];
};

const signOffShift = async () => {
  signOffLoading.value = true;
  const payload = {};
  if (startEnd.value[0] > 0) {
    payload.start_at = startTime.value;
  }
  if (startEnd.value[1] < totalMinutes.value) {
    payload.end_at = endTime.value;
  }
  try {
    await ApiClient.addTalentPoolShiftClaimRating(props.shift?.assigned_to, props.shift?.shift_id, rating.value, {
      tags: selectedReviewTags.value.toString(),
    });
    await ApiClient.signOffTalentPoolShiftClaim(props.shift?.assigned_to, payload);
    const shifts = await getShiftsForTab(locationId, tab, true);
    if (shifts.length > 0) {
      router.push({
        name: 'talent-pool-shift-detail',
        params: { locationId, tab, shiftId: shifts[0].shift_id },
      });
    } else {
      router.push({
        name: 'talent-pool-shift-status',
        params: { locationId, tab },
      });
    }
  } finally {
    signOffLoading.value = false;
  }
};
</script>

<style scoped lang="scss">
.sign-off {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.sign-off .heading {
  display: flex;
  flex-direction: column;
}

.sign-off .heading .title {
  font-size: 22px;
  font-weight: 400;
}

.sign-off .heading .date {
  font-size: 14px;
  font-weight: 300;
}

.sign-off .sign-off-container {
  background-color: var(--palette-color-base-white);
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
}

.sign-off .sign-off-container footer {
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--palette-color-default-lighten-90);
}

@media (max-width: 900px) {
  .sign-off .sign-off-container footer {
    justify-content: flex-end;
  }
}

.sign-off .sign-off-container footer .user-details {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sign-off .sign-off-container footer .user-details .details {
  display: flex;
  flex-direction: column;
}

@media (max-width: 900px) {
  .sign-off .sign-off-container footer .user-details {
    display: none;
  }
}

.sign-off .sign-off-container footer .user-details .details .name {
  font-size: 18px;
  font-weight: 500;
}

.sign-off .sign-off-container footer .confirmation {
  display: flex;
  align-items: center;
  gap: 1rem;
}

@media (max-width: 900px) {
  .sign-off .sign-off-container footer .confirmation {
    width: 100%;
    justify-content: space-between;
  }
}

.sign-off .sign-off-container footer .confirmation .pay {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sign-off .sign-off-container footer .confirmation .pay .old-total {
  font-size: 18px;
  font-weight: 400;
  text-decoration: line-through;
}

.sign-off .sign-off-container footer .confirmation .pay .new-total {
  font-size: 18px;
  font-weight: 600;
}

.sign-off .sign-off-container .shift-results {
  display: flex;
}

@media (max-width: 900px) {
  .sign-off .sign-off-container .shift-results {
    flex-direction: column;
  }
}

.sign-off .sign-off-container .shift-results .shift-result {
  padding: 1.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  border-right: 1px solid var(--palette-color-default-lighten-90);
}

@media (max-width: 900px) {
  .sign-off .sign-off-container .shift-results .shift-result {
    border-right: none;
    border-bottom: 1px solid var(--palette-color-default-lighten-90);
  }
}

.sign-off .sign-off-container .shift-results .shift-result h3 {
  font-size: 18px;
  font-weight: 500;
}

.sign-off .sign-off-container .shift-results .shift-result .verification-code {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sign-off .sign-off-container .shift-results .shift-result .verification-code > span {
  font-size: 16px;
  font-weight: 400;
}

.sign-off .sign-off-container .shift-results .shift-result .verification-code input {
  border-radius: 0.3rem;
  border: 1px solid var(--palette-color-default-lighten-90);
  padding: 10px 12px;
  width: 126px;
}

.sign-off .sign-off-container .shift-results .shift-result hr {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
  margin: 0;
}

.sign-off .sign-off-container .shift-results .shift-result .time-confirm {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.sign-off .sign-off-container .shift-results .shift-result .time-confirm .toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sign-off .sign-off-container .shift-results .shift-result .time-confirm .time-select {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid var(--palette-color-default-lighten-90);
  gap: 1rem;
}

.sign-off .sign-off-container .shift-results .shift-result .time-confirm .time-select .labels {
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  font-weight: 500;
}

.sign-off .sign-off-container .shift-results .shift-result .time-confirm .time-select .times {
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  font-weight: 300;
}

.sign-off .sign-off-container .shift-results .shift-result .time-confirm .time-select .times > div {
  display: flex;
  gap: 0.5rem;
  text-transform: lowercase;
}

.sign-off .sign-off-container .shift-results .shift-result .time-confirm .time-select .times .expected.not {
  color: var(--palette-color-danger);
  text-decoration: line-through;
}

.sign-off .sign-off-container .shift-results .shift-result .time-confirm .toggle > span {
  font-size: 16px;
  font-weight: 400;
}

.sign-off .sign-off-container .shift-results .shift-result .time-confirm .toggle > div {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.sign-off .sign-off-container .shift-results .employee-result {
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 35%;
}

@media (max-width: 900px) {
  .sign-off .sign-off-container .shift-results .employee-result {
    width: 100%;
  }
}

.sign-off .sign-off-container .shift-results .employee-result .star-explainer {
  display: flex;
  align-items: center;
  flex-direction: column;
}

.sign-off .sign-off-container .shift-results .employee-result h3 {
  font-size: 18px;
  font-weight: 500;
  margin: 0;
}

.sign-off .sign-off-container .shift-results .employee-result .rating {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
}

.sign-off .sign-off-container .shift-results .employee-result .rating .rating-label {
  font-size: 16px;
  font-weight: 400;
}

.sign-off .sign-off-container .shift-results .employee-result .stars {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.sign-off .sign-off-container .shift-results .employee-result .stars .rating-star {
  color: var(--palette-color-default-lighten-80);
  transition: transform 0.3s ease, color 0.3s ease;
  /* Smooth transition for color and transform */
}

.sign-off .sign-off-container .shift-results .employee-result .stars .rating-star:hover {
  color: var(--palette-color-warning-lighten-30);
  cursor: pointer;
  /* Indicates that the star is clickable */
}

.sign-off .sign-off-container .shift-results .employee-result .stars .rating-star:has(~ .rating-star:hover) {
  color: var(--palette-color-warning-lighten-30);
  cursor: pointer;
  /* Indicates that the star is clickable */
}

.sign-off .sign-off-container .shift-results .employee-result .stars .rating-star.active {
  color: var(--palette-color-warning);
  transform: scale(1.1);
  /* Slightly increase the size */
  animation: pop 0.3s ease;
  /* Apply the animation */
}

@keyframes pop {
  0% {
    transform: scale(1);
    /* Start with the original size */
  }

  50% {
    transform: scale(1.2);
    /* Scale up to a bit larger than the active size */
  }

  100% {
    transform: scale(1.1);
    /* End with the active size */
  }
}

.sign-off .sign-off-container .shift-results .employee-result span {
  font-size: 14px;
  font-weight: 300;
}

.sign-off .sign-off-container .shift-results .employee-result .tags-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  border-top: 1px solid var(--palette-color-default-lighten-90);
  padding-top: 1rem;
}

.sign-off .sign-off-container .shift-results .employee-result .tags-container > span {
  font-size: 16px;
  font-weight: 500;
}

.sign-off .sign-off-container .shift-results .employee-result .tags-container .review-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
}

.sign-off .sign-off-container .shift-results .employee-result .tags-container .review-tags .tag {
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-basis: auto;
  flex-shrink: 1;
  border: 2px solid var(--palette-color-base-white);
  transition: 0.2s;
}

.sign-off .sign-off-container .shift-results .employee-result .tags-container .review-tags .tag.active {
  border: 2px solid var(--palette-color-brand-primary);
}

.sign-off .sign-off-container .shift-results .employee-result .tags-container .review-tags .tag label {
  font-size: 16px;
  font-weight: 300;
  margin: 0;
  text-transform: capitalize;
}
</style>
